import TeacherPageHeader from '../../components/TeacherPageHeader';
import React, { useState, useEffect, useCallback } from 'react';
import { useAtomValue } from 'jotai';
import toast from 'react-hot-toast';
import { authState } from '../../store/atoms';
import { practiceAPI, calendarAPI, holidayAPI } from '../../services/index';
import { format, parseISO, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';
import { ko, enUS } from 'date-fns/locale';
import SimpleCalendar from '../../components/SimpleCalendar';
import { CalendarEvent, User, Holiday } from '../../types/api';
import { UnifiedScheduleEvent } from '../../services/calendarAPI';
import { DEFAULT_TEACHER_ID } from '../../config/constants';
import { useLanguage } from '../../contexts/LanguageContext';

const UserPracticeBooking: React.FC = () => {
  const auth = useAtomValue(authState);
  const { t, language } = useLanguage();
  const dateLocale = language === 'ENG' ? enUS : ko;
  const [selectedDate, setSelectedDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [selectedDateBookings, setSelectedDateBookings] = useState<any[]>([]);

  // 캘린더 관련 상태 (대시보드와 동일)
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date>(new Date());
  const [selectedDateEvents, setSelectedDateEvents] = useState<CalendarEvent[]>([]);

  // 예약 관련 상태
  const [showBookingForm, setShowBookingForm] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedStartTime, setSelectedStartTime] = useState<string>('');
  const [selectedEndTime, setSelectedEndTime] = useState<string>('');
  const [bookingNotes, setBookingNotes] = useState<string>('');

  // 휴일 관련 상태
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // 시간대 설정
  // 24시간 운영: 00:00 ~ 23:00 시작, 종료는 최대 24:00(다음 날 00:00)
  const timeSlots = Array.from({ length: 24 }, (_, h) => `${h.toString().padStart(2, '0')}:00`);

  // 시작 시간 순서값 (당일 기준 0~23)
  const slotOrder = (time: string) => parseInt(time.split(':')[0]);

  // 종료 시간 순서값 — '00:00'은 다음 날 자정(24)으로 취급
  const endSlotOrder = (time: string) => {
    const hour = parseInt(time.split(':')[0]);
    return hour === 0 ? 24 : hour;
  };

  // 순서값을 다시 'HH:00' 형식으로 변환 (24 -> '00:00')
  const orderToSlot = (order: number) => `${(order % 24).toString().padStart(2, '0')}:00`;

  // 선택된 날짜와 시간으로 Date 생성 (종료가 '00:00'이면 다음 날 자정)
  const buildDateTime = (time: string, isEnd = false) => {
    const date = new Date(`${selectedDate}T${time}`);
    if (isEnd && parseInt(time.split(':')[0]) === 0) {
      date.setDate(date.getDate() + 1);
    }
    return date;
  };

  // 휴일 데이터 로드
  const fetchHolidays = useCallback(async (targetDate: Date) => {
    try {
      const response = await holidayAPI.getHolidays({
        year: targetDate.getFullYear(),
        month: targetDate.getMonth() + 1
      });
      if (response.data?.success && response.data?.data) {
        setHolidays(response.data.data);
      }
    } catch {
      // 휴일 로드 실패
    }
  }, []);

  // 휴일 로드 useEffect
  useEffect(() => {
    fetchHolidays(currentMonth);
  }, [currentMonth, fetchHolidays]);

  // 캘린더 데이터 로드 (대시보드와 동일)
  const loadCalendarData = async (targetDate?: Date) => {
    if (!auth.userInfo) return;

    try {
      const baseDate = targetDate || new Date();
      const startDate = format(startOfMonth(baseDate), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(baseDate), 'yyyy-MM-dd');

      // 수업 일정 조회
      const scheduleResponse = await calendarAPI.getUnifiedCalendar({
        startDate,
        endDate,
        teacherId: DEFAULT_TEACHER_ID,
        userId: (auth.userInfo as User).id,
        includeRegular: true,
        includeModifications: true
      });

      // 연습실 예약 조회
      interface PracticeBookingData {
        id: number;
        startTime: string;
        endTime: string;
        practiceRoom: string;
        status?: string;
      }
      let practiceBookings: PracticeBookingData[] = [];
      try {
        const practiceResponse = await fetch(`/api/practice/my-sessions?startDate=${startDate}&endDate=${endDate}`, {
          credentials: 'include'
        });
        if (practiceResponse.ok) {
          const practiceData = await practiceResponse.json();
          if (practiceData.success && practiceData.data) {
            practiceBookings = practiceData.data.sessions || [];
          }
        }
      } catch {
        // Practice bookings load failed silently
      }

      const response = scheduleResponse;

      if (response.data && response.data.success) {
        const eventsByDate = response.data.data.events || [];

        const allSchedules: UnifiedScheduleEvent[] = [];
        eventsByDate.forEach((dayEvent: { date: string; schedules?: UnifiedScheduleEvent[] }) => {
          if (dayEvent.schedules && Array.isArray(dayEvent.schedules)) {
            dayEvent.schedules.forEach((schedule: UnifiedScheduleEvent) => {
              allSchedules.push({
                ...schedule,
                date: dayEvent.date
              });
            });
          }
        });

        const calendarEventsList: CalendarEvent[] = [];

        // 수업 일정 추가
        allSchedules.forEach((s) => {
          const eventDate = new Date(s.date);
          const [startHour, startMinute] = s.startTime.split(':');
          const [endHour, endMinute] = s.endTime.split(':');

          const startTime = new Date(eventDate);
          startTime.setHours(parseInt(startHour), parseInt(startMinute), 0);

          const endTime = new Date(eventDate);
          endTime.setHours(parseInt(endHour), parseInt(endMinute), 0);

          calendarEventsList.push({
            id: `lesson_${s.id}`,
            title: s.title || s.studentName || t('lesson_default_title'),
            type: 'lesson',
            start: startTime,
            end: endTime,
            color: s.scheduleType === 'regular' ? '#4CAF50' :
              s.scheduleType === 'rescheduled' ? '#FF9800' :
                s.scheduleType === 'cancelled' ? '#F44336' : '#2196F3',
            data: { ...s, eventType: 'lesson' }
          });
        });

        // 연습실 예약 추가 (취소된 예약 제외)
        practiceBookings
          .filter((booking: PracticeBookingData) => booking.status !== 'cancelled')
          .forEach((booking: PracticeBookingData) => {
            const startTime = new Date(booking.startTime);
            const endTime = new Date(booking.endTime);

            calendarEventsList.push({
              id: `practice_${booking.id}`,
              title: `${t('practice_room_booking_label')} (${booking.practiceRoom})`,
              type: 'practice',
              start: startTime,
              end: endTime,
              color: '#9C27B0',
              data: { ...booking, eventType: 'practice' }
            });
          });

        // 납부일 이벤트 추가
        if (auth.userInfo && (auth.userInfo as User).paymentDayOfMonth) {
          const paymentDay = (auth.userInfo as User).paymentDayOfMonth!;
          const paymentDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), paymentDay);

          if (paymentDate.getMonth() === baseDate.getMonth()) {
            calendarEventsList.push({
              id: `payment_${paymentDate.toISOString()}`,
              title: t('payment_day_event'),
              type: 'personal',
              start: paymentDate,
              end: paymentDate,
              color: '#E91E63',
              data: { eventType: 'payment', paymentDay }
            });
          }
        }

        setCalendarEvents(calendarEventsList);
      }
    } catch {
      // Calendar data load failed silently
    }
  };

  // 선택된 날짜의 이벤트 업데이트
  const updateSelectedDateEvents = (date: Date) => {
    const events = calendarEvents
      .filter(event => {
        const eventDate = new Date(event.start);
        return eventDate.toDateString() === date.toDateString();
      })
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    setSelectedDateEvents(events);
  };

  // 캘린더 데이터 로드 및 오늘 날짜 예약 정보 로드
  useEffect(() => {
    if (auth.isAuthenticated && auth.userType === 'user' && auth.userInfo) {
      loadCalendarData();
      // 오늘 날짜의 예약 정보 로드
      loadSelectedDateBookings(format(new Date(), 'yyyy-MM-dd'));
    }
  }, [auth.isAuthenticated, auth.userType, auth.userInfo]);

  // calendarEvents가 변경되면 선택된 날짜의 이벤트 업데이트
  useEffect(() => {
    if (calendarEvents.length > 0) {
      updateSelectedDateEvents(selectedCalendarDate);
    }
  }, [calendarEvents]);

  // 선택된 날짜의 연습실 예약을 가져오는 함수
  const loadSelectedDateBookings = async (date: string) => {
    if (!date) return;

    try {
      const response = await practiceAPI.getBookings({
        startDate: date,
        endDate: date,
      });

      if (response.success && response.data && 'sessions' in response.data) {
        setSelectedDateBookings((response.data as any).sessions || []);
      }
    } catch {
      // 연습실 예약 조회 실패
    }
  };





  const handleDateClick = (date: Date, dayEvents?: CalendarEvent[]) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setSelectedDate(dateStr);
    setSelectedCalendarDate(date);

    // 선택된 날짜의 이벤트 업데이트
    if (dayEvents) {
      setSelectedDateEvents(dayEvents.sort((a, b) =>
        new Date(a.start).getTime() - new Date(b.start).getTime()
      ));
    } else {
      updateSelectedDateEvents(date);
    }

    // 선택된 날짜가 오늘 이후인 경우에만 예약 폼 표시
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateObj = new Date(date);
    selectedDateObj.setHours(0, 0, 0, 0);

    if (selectedDateObj >= today) {
      setShowBookingForm(true);
      // 시간 선택 초기화
      setSelectedStartTime('');
      setSelectedEndTime('');
      setBookingNotes('');

      // 선택된 날짜의 예약 정보 로드
      loadSelectedDateBookings(dateStr);
    } else {
      setShowBookingForm(false);
    }
  };

  // 특정 시간대가 사용 불가능한지 확인하는 함수
  const isTimeSlotDisabled = (timeSlot: string) => {
    // 선택된 날짜에 이미 예약이 있는지 확인
    const hasExistingBooking = selectedDateBookings.some(booking => {
      if (booking.status === 'cancelled') return false; // 취소된 예약은 제외

      const bookingStart = new Date(booking.startTime);
      const bookingEnd = new Date(booking.endTime);
      const slotTime = new Date(`${selectedDate}T${timeSlot}`);

      // 당일 0시 기준 분 단위 비교 (자정 넘김은 날짜 차이로 보정)
      const slotMinutes = slotTime.getHours() * 60;
      const startMinutes = bookingStart.getHours() * 60 + bookingStart.getMinutes();
      let endMinutes = bookingEnd.getHours() * 60 + bookingEnd.getMinutes();
      if (bookingEnd.toDateString() !== bookingStart.toDateString()) {
        endMinutes += 24 * 60;
      }

      // 해당 시간대가 예약 시간 범위에 포함되는지 확인
      return slotMinutes >= startMinutes && slotMinutes < endMinutes;
    });

    return hasExistingBooking;
  };

  // 시작 시간부터 연속으로 예약 가능한 종료 상한(순서값).
  // 시작 이후 처음 마감된 슬롯을 만나면 그 지점까지만 예약 가능(마감 슬롯을 넘어설 수 없음).
  const maxEndOrderFrom = (startTime: string): number => {
    const start = slotOrder(startTime);
    for (let o = start + 1; o < 24; o++) {
      if (isTimeSlotDisabled(orderToSlot(o))) return o;
    }
    return 24;
  };

  // 시간 버튼 생성 함수
  const generateTimeButtons = () => {
    return timeSlots.map(time => {
      const isDisabled = isTimeSlotDisabled(time);
      const isStartSelected = selectedStartTime === time;
      const isEndSelected = selectedEndTime === time;
      const isInRange = selectedStartTime && selectedEndTime &&
        slotOrder(time) > slotOrder(selectedStartTime) && slotOrder(time) < endSlotOrder(selectedEndTime);

      return (
        <button
          key={time}
          type="button"
          className={`time-button ${isStartSelected ? 'start-selected' : ''} ${isEndSelected ? 'end-selected' : ''} ${isInRange ? 'in-range' : ''} ${isDisabled ? 'disabled' : ''}`}
          onClick={() => handleTimeButtonClick(time)}
          disabled={isDisabled}
          title={isDisabled ? t('time_already_booked') : ''}
        >
          {time}
        </button>
      );
    });
  };

  // 시간 버튼 클릭 처리
  // 시작 시간을 선택하고 종료 시간을 1시간 뒤로 자동 설정
  const selectStartTime = (time: string) => {
    setSelectedStartTime(time);
    // 종료는 시작 +1시간(단, 마감 슬롯/24:00을 넘지 않음)
    const endOrder = Math.min(slotOrder(time) + 1, maxEndOrderFrom(time));
    setSelectedEndTime(orderToSlot(endOrder));
  };

  const handleTimeButtonClick = (time: string) => {
    if (!selectedStartTime) {
      // 시작 시간 선택
      selectStartTime(time);
    } else if (selectedStartTime === time) {
      // 시작 시간을 다시 클릭하면 선택 해제
      setSelectedStartTime('');
      setSelectedEndTime('');
    } else if (slotOrder(time) > slotOrder(selectedStartTime)) {
      // 종료 시간 선택 — 시작~종료 사이에 마감 슬롯이 있으면 그 앞까지만 예약 가능.
      // 마감 시간을 넘어서는 예약은 서버에서 거부되므로, 전/후를 따로 예약해야 한다.
      const cap = maxEndOrderFrom(selectedStartTime);
      const chosen = Math.min(endSlotOrder(time), cap);
      setSelectedEndTime(orderToSlot(chosen));
    } else {
      // 시작 시간보다 이전 시간을 클릭하면 새로운 시작 시간으로 설정
      selectStartTime(time);
    }
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDate || !selectedStartTime || !selectedEndTime) {
      toast.error(t('select_date_and_time'));
      return;
    }

    try {
      setIsSubmitting(true);

      const startDateTime = buildDateTime(selectedStartTime);
      const endDateTime = buildDateTime(selectedEndTime, true);

      // 시작 시간이 종료 시간보다 늦은 경우
      if (startDateTime >= endDateTime) {
        toast.error(t('start_before_end'));
        return;
      }

      // 최소 예약 시간이 1시간인지 확인
      const timeDiff = endDateTime.getTime() - startDateTime.getTime();
      const minBookingTime = 60 * 60 * 1000; // 1시간을 밀리초로
      if (timeDiff < minBookingTime) {
        toast.error(t('minimum_one_hour'));
        return;
      }

      // 현재 시간보다 이전인 경우
      if (startDateTime <= new Date()) {
        toast.error(t('no_past_booking'));
        return;
      }

      const response = await practiceAPI.bookRoom({
        roomId: 1,
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
        notes: bookingNotes.trim() || undefined
      });

      if (response.success) {
        toast.success(t('booking_success'));
        setShowBookingForm(false);
        setSelectedStartTime('');
        setSelectedEndTime('');
        setBookingNotes('');
        loadSelectedDateBookings(selectedDate); // 선택된 날짜의 예약 정보 새로고침
        loadCalendarData(); // 캘린더 데이터 새로고침
      } else {
        toast.error(response.message || t('booking_failed'));
      }
    } catch {
      toast.error(t('booking_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelBooking = async (sessionId: string) => {
    if (!window.confirm(t('confirm_cancel_booking'))) {
      return;
    }

    try {
      const response = await practiceAPI.cancelBooking(sessionId);

      if (response.success) {
        toast.success(t('booking_cancelled'));
        loadSelectedDateBookings(selectedDate); // 선택된 날짜의 예약 정보 새로고침
        loadCalendarData(); // 캘린더 데이터 새로고침
      } else {
        toast.error(response.message || t('cancel_booking_failed'));
      }
    } catch {
      toast.error(t('cancel_booking_error'));
    }
  };

  return (
    <div className="user-page user-lesson-schedule-page practice-booking-page">
      <TeacherPageHeader title="연습실 예약">
        <div className="month-nav">
          <button
            type="button"
            className="month-nav-btn"
            aria-label="이전 달"
            onClick={() => { const d = subMonths(currentMonth, 1); setCurrentMonth(d); loadCalendarData(d); }}
          >
            ‹
          </button>
          <span className="month-label">{format(currentMonth, language === 'ENG' ? 'MMM yyyy' : 'yyyy년 M월', { locale: dateLocale })}</span>
          <button
            type="button"
            className="month-nav-btn"
            aria-label="다음 달"
            onClick={() => { const d = addMonths(currentMonth, 1); setCurrentMonth(d); loadCalendarData(d); }}
          >
            ›
          </button>
        </div>
        <div className="calendar-legend">
          <span className="legend-item"><span className="legend-swatch" style={{ background: '#1e56d6' }} />내 예약</span>
          <span className="legend-item"><span className="legend-swatch" style={{ background: '#94a3b8' }} />예약 마감</span>
        </div>
      </TeacherPageHeader>
      <div className="page-content">
        <div className="container">
          <div className="form-container">
            {/* 2단 레이아웃: 왼쪽 캘린더, 오른쪽 예약 폼 */}
            <div className="practice-booking-grid">
              {/* 왼쪽: 캘린더 */}
              <div className="form-section calendar-section">
                <SimpleCalendar
                  hideHeader
                  date={currentMonth}
                  events={calendarEvents}
                  holidays={holidays}
                  onDateClick={handleDateClick}
                  onMonthChange={(date) => {
                    setCurrentMonth(date);
                    loadCalendarData(date);
                  }}
                  selectedDate={selectedCalendarDate}
                />
              </div>

              {/* 오른쪽: 예약 폼 */}
              <div className="booking-form-wrapper">
                {/* 모바일에서 선택된 날짜의 일정 표시 - Booking 라벨 위에 */}
                {selectedDateEvents.length > 0 && (
                  <div className="mobile-date-events">
                    {selectedDateEvents.map((event, idx) => (
                      <span key={idx} className={`event-chip ${event.type}`}>
                        {format(new Date(event.start), 'HH:mm')} {event.title}
                      </span>
                    ))}
                  </div>
                )}

                {showBookingForm && selectedDate ? (
                  <div className="form-section">
                    <div className="booking-head">
                      <div className="booking-title">
                        {format(new Date(selectedDate), language === 'ENG' ? 'MMM d (E)' : 'M. d (E)', { locale: dateLocale })} 예약
                      </div>
                      <div className="booking-sub">{t('minimum_one_hour_notice')}</div>
                    </div>

                    <form onSubmit={handleBookingSubmit}>
                      <div className="form-group">
                        <label className="form-label">{t('select_time')}</label>
                        {!selectedStartTime && (
                          <p className="time-hint">{t('click_start_time')}</p>
                        )}
                        {selectedStartTime && selectedEndTime && (
                          <p className="time-hint">{t('click_to_change_end_time')}</p>
                        )}
                        <div className="time-button-group">
                          {generateTimeButtons()}
                        </div>
                        {selectedStartTime && selectedEndTime && (
                          <div className="change-preview">
                            <div className="preview-item">
                              <span className="preview-label">{t('selected_time_label')}</span>
                              <span className="preview-value">
                                {selectedStartTime} - {selectedEndTime}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="form-actions">
                        <button
                          type="submit"
                          className="btn btn-primary btn-sm"
                          disabled={isSubmitting || !selectedStartTime || !selectedEndTime}
                        >
                          {isSubmitting ? `${t('booking_for_date')}...` : t('book_now')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            setSelectedStartTime('');
                            setSelectedEndTime('');
                            setBookingNotes('');
                          }}
                        >
                          {t('reset')}
                        </button>
                      </div>
                    </form>
                  </div>
                ) : null}

                {/* 내 예약 목록 */}
                {selectedDateBookings.filter(b =>
                  b.status !== 'cancelled' &&
                  (b.user?.id === (auth.userInfo as User)?.id || b.userId === (auth.userInfo as User)?.id)
                ).length > 0 && (
                    <div className="form-section my-bookings-section" style={{ marginTop: '1rem' }}>
                      <div className="section-header">
                        <div className="section-label">My Bookings</div>
                        <h2 className="section-title">{t('practice_room_booking_label')}</h2>
                      </div>
                      <div className="my-bookings-list">
                        {selectedDateBookings
                          .filter(b =>
                            b.status !== 'cancelled' &&
                            (b.user?.id === (auth.userInfo as User)?.id || b.userId === (auth.userInfo as User)?.id)
                          )
                          .map((booking) => (
                            <div key={booking.id} className="booking-item">
                              <div className="booking-info">
                                <span className="booking-time">
                                  {format(parseISO(booking.startTime), 'HH:mm')} - {format(parseISO(booking.endTime), 'HH:mm')}
                                </span>
                                <span className="booking-room">
                                  {booking.practiceRoom === 'room1' ? t('drum_practice_room_1') :
                                    booking.practiceRoom === 'room2' ? t('drum_practice_room_2') :
                                      booking.practiceRoom === 'room3' ? t('drum_practice_room_3') : booking.practiceRoom}
                                </span>
                              </div>
                              {booking.status === 'scheduled' && new Date(booking.startTime) > new Date() && (
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleCancelBooking(booking.sessionId || booking.id)}
                                >
                                  {t('cancel')}
                                </button>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPracticeBooking; 